import {isValid} from "date-fns";

import {formatPL} from "@pg-mono/string-utils";

import {Country} from "../../../../region/types/Country";
import {RegionType} from "../../../../region/types/RegionType";
import {OfferType} from "../../../types/OfferType";
import {ISelectedRegion} from "../../actions/offer_list_selected_data_actions";
import {IOfferListRoomsFilter} from "./constants";
import {isExactMultiRoomFlatFilter, isExactStudioFlatFilter, isOpenEndedRoomsFilter} from "./get_location_generic_description";

interface IParams {
    offerType: OfferType | null;
    filters?: IOfferListRoomsFilter | null;
    region: ISelectedRegion;
    url: string;
    // "dd.MM.yyyy" — the same "Dane aktualne na..." string already shown in the visible content (flat offers
    // only). Parsed back into a Date so datePublished/dateModified/temporalCoverage match it exactly.
    updateDate?: string;
}

interface IDatasetVariableMeasured {
    "@type": "PropertyValue";
    name: string;
    value: number;
    unitText: string;
    description?: string;
}

interface IOfferListDatasetSchema {
    "@context": "https://schema.org/";
    "@type": "Dataset";
    name: string;
    description: string;
    url: string;
    keywords: string[];
    inLanguage: "pl-PL";
    isAccessibleForFree: true;
    datePublished: string;
    dateModified: string;
    temporalCoverage: string;
    creator: {"@type": "Organization"; name: string; url: string};
    publisher: {"@type": "Organization"; name: string; url: string};
    spatialCoverage: {
        "@type": "Place";
        name: string;
        address: {
            "@type": "PostalAddress";
            addressLocality: string;
            addressRegion?: string;
            addressCountry: "PL";
        };
    };
    variableMeasured: IDatasetVariableMeasured[];
}

interface IOfferTypeWording {
    nounPlural: string;
    nounGenitivePlural: string;
}

const OFFER_TYPE_WORDING: Record<"FLAT" | "HOUSE" | "COMMERCIAL" | "MIXED", IOfferTypeWording> = {
    FLAT: {nounPlural: "nowe mieszkania", nounGenitivePlural: "mieszkań"},
    HOUSE: {nounPlural: "nowe domy", nounGenitivePlural: "domów"},
    COMMERCIAL: {nounPlural: "nowe lokale użytkowe", nounGenitivePlural: "lokali użytkowych"},
    MIXED: {nounPlural: "nowe nieruchomości", nounGenitivePlural: "nieruchomości"}
};

const pushIfPositive = (entries: IDatasetVariableMeasured[], value: number | null | undefined, entry: Omit<IDatasetVariableMeasured, "value">) => {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        entries.push({...entry, value});
    }
};

const RP_ORGANIZATION = {"@type": "Organization" as const, name: "RynekPierwotny.pl", url: "https://rynekpierwotny.pl/"};

const buildBaseSchema = (
    region: ISelectedRegion,
    wording: IOfferTypeWording,
    url: string,
    reportDate: Date
): Omit<IOfferListDatasetSchema, "variableMeasured"> => {
    const voivodeship = region.stats?.region_type_voivodeship?.name;
    const dateIso = formatPL(reportDate, "yyyy-MM-dd");

    return {
        "@context": "https://schema.org/",
        "@type": "Dataset",
        name: `Raport rynkowy – ${wording.nounPlural} ${region.name}, ${formatPL(reportDate, "LLLL yyyy")}`,
        description: `Aktualny stan rynku pierwotnego (${wording.nounGenitivePlural}) ${region.name_declension_where}: liczba ofert i inwestycji deweloperskich, ceny (w tym cena za m²), metraże oraz terminy oddania. Dane aktualne na ${dateIso}.`,
        url,
        keywords: [
            `${wording.nounPlural} ${region.name}`,
            `rynek pierwotny ${region.name}`,
            `ceny ${wording.nounGenitivePlural} ${region.name}`,
            `${wording.nounPlural} od dewelopera ${region.name}`,
            `cena za m2 ${region.name}`
        ],
        inLanguage: "pl-PL",
        isAccessibleForFree: true,
        datePublished: dateIso,
        dateModified: dateIso,
        temporalCoverage: dateIso,
        creator: RP_ORGANIZATION,
        publisher: RP_ORGANIZATION,
        spatialCoverage: {
            "@type": "Place",
            name: `${region.name}, Polska`,
            address: {
                "@type": "PostalAddress",
                addressLocality: region.name,
                addressRegion: voivodeship,
                addressCountry: "PL"
            }
        }
    };
};

const getFlatVariableMeasured = (region: ISelectedRegion): IDatasetVariableMeasured[] => {
    const stats = region.stats;
    const entries: IDatasetVariableMeasured[] = [];

    if (!stats?.offers_flats_count_for_sale || !stats?.properties_flats_count_for_sale) {
        return entries;
    }

    pushIfPositive(entries, stats.properties_flats_count_for_sale, {
        "@type": "PropertyValue",
        name: "Liczba ofert nowych mieszkań w sprzedaży",
        unitText: "oferty"
    });
    pushIfPositive(entries, stats.offers_flats_count_for_sale, {"@type": "PropertyValue", name: "Liczba inwestycji deweloperskich", unitText: "inwestycje"});
    pushIfPositive(entries, stats.average_price_m2, {"@type": "PropertyValue", name: "Średnia cena za metr kwadratowy", unitText: "PLN/m2"});
    pushIfPositive(entries, stats.properties_flats_ranges_price_min, {"@type": "PropertyValue", name: "Najniższa cena mieszkania", unitText: "PLN"});

    if (
        typeof stats.average_price_m2_y2y_change === "number" &&
        Number.isFinite(stats.average_price_m2_y2y_change) &&
        stats.average_price_m2_y2y_change !== 0
    ) {
        entries.push({
            "@type": "PropertyValue",
            name: "Zmiana średniej ceny rok do roku",
            value: stats.average_price_m2_y2y_change,
            unitText: "PERCENT"
        });
    }

    pushIfPositive(entries, stats.properties_flats_ranges_area_min, {"@type": "PropertyValue", name: "Metraż minimalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_flats_ranges_area_max, {"@type": "PropertyValue", name: "Metraż maksymalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_flats_count_ready, {"@type": "PropertyValue", name: "Mieszkania gotowe do odbioru", unitText: "lokale"});

    const inConstruction =
        (stats.properties_flats_count_ready_in_3m || 0) + (stats.properties_flats_count_ready_in_6m || 0) + (stats.properties_flats_count_ready_in_12m || 0);
    pushIfPositive(entries, inConstruction, {"@type": "PropertyValue", name: "Mieszkania w trakcie budowy", unitText: "lokale"});

    pushDominantRoomType(entries, stats.properties_flats_rooms_ranges, stats.properties_flats_count_for_sale, (roomCount) =>
        roomCount === 1 ? "Kawalerki" : getRoomTypeLabel("Mieszkania", roomCount)
    );

    pushIfPositive(entries, stats.promotions_count_total, {"@type": "PropertyValue", name: "Liczba aktualnych ofert promocyjnych", unitText: "oferty"});

    return entries;
};

// "5-pokojowe i większe" mirrors how the visible content groups the top room-count bucket for every offer type.
const getRoomTypeLabel = (nounPlural: string, roomCount: number): string =>
    roomCount >= 5 ? `${nounPlural} 5-pokojowe i większe` : `${nounPlural} ${roomCount}-pokojowe`;

// Percentage share isn't spelled out for house/commercial the way it is for flats, but the same per-room-count
// counts ARE already listed on the page (e.g. "domy dwupokojowe - X domów") — this only summarizes that same data.
// Room counts of 5+ can arrive under separate keys (6, 7, 8...) rather than one bucket, so they're folded into "5".
const pushDominantRoomType = (
    entries: IDatasetVariableMeasured[],
    roomRanges: Record<number, number> | undefined,
    total: number | undefined,
    getLabel: (roomCount: number) => string
) => {
    const buckets: Record<number, number> = {};

    Object.entries(roomRanges || {}).forEach(([key, count]) => {
        const room = Number(key);
        if (!Number.isFinite(room) || typeof count !== "number") {
            return;
        }
        const bucket = Math.min(room, 5);
        buckets[bucket] = (buckets[bucket] || 0) + count;
    });

    const dominantRoom = Object.entries(buckets).reduce<{room: number; count: number}>(
        (max, [room, count]) => (count > max.count ? {room: Number(room), count} : max),
        {room: 0, count: 0}
    );

    if (dominantRoom.room > 0 && total && total > 0) {
        entries.push({
            "@type": "PropertyValue",
            name: "Udział najpopularniejszego typu w ofercie",
            value: Number(((dominantRoom.count / total) * 100).toFixed(1)),
            unitText: "PERCENT",
            description: getLabel(dominantRoom.room)
        });
    }
};

const getHouseVariableMeasured = (region: ISelectedRegion): IDatasetVariableMeasured[] => {
    const stats = region.stats;
    const entries: IDatasetVariableMeasured[] = [];

    if (!stats?.offers_houses_count_for_sale || !stats?.properties_houses_count_for_sale) {
        return entries;
    }

    pushIfPositive(entries, stats.properties_houses_count_for_sale, {
        "@type": "PropertyValue",
        name: "Liczba ofert nowych domów w sprzedaży",
        unitText: "oferty"
    });
    pushIfPositive(entries, stats.offers_houses_count_for_sale, {"@type": "PropertyValue", name: "Liczba inwestycji deweloperskich", unitText: "inwestycje"});
    pushIfPositive(entries, stats.average_price_m2, {"@type": "PropertyValue", name: "Średnia cena za metr kwadratowy", unitText: "PLN/m2"});
    pushIfPositive(entries, stats.properties_houses_ranges_price_min, {"@type": "PropertyValue", name: "Najniższa cena domu", unitText: "PLN"});
    pushIfPositive(entries, stats.properties_houses_ranges_area_min, {"@type": "PropertyValue", name: "Metraż minimalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_houses_ranges_area_max, {"@type": "PropertyValue", name: "Metraż maksymalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_houses_count_ready, {"@type": "PropertyValue", name: "Domy gotowe do odbioru", unitText: "lokale"});

    const inConstruction =
        (stats.properties_houses_count_ready_in_3m || 0) + (stats.properties_houses_count_ready_in_6m || 0) + (stats.properties_houses_count_ready_in_12m || 0);
    pushIfPositive(entries, inConstruction, {"@type": "PropertyValue", name: "Domy w trakcie budowy", unitText: "lokale"});

    pushDominantRoomType(entries, stats.properties_houses_rooms_ranges, stats.properties_houses_count_for_sale, (roomCount) =>
        getRoomTypeLabel("Domy", roomCount)
    );

    pushIfPositive(entries, stats.promotions_count_total, {"@type": "PropertyValue", name: "Liczba aktualnych ofert promocyjnych", unitText: "oferty"});

    return entries;
};

const getCommercialVariableMeasured = (region: ISelectedRegion): IDatasetVariableMeasured[] => {
    const stats = region.stats;
    const entries: IDatasetVariableMeasured[] = [];

    if (!stats?.offers_commercials_count_for_sale || !stats?.properties_commercials_count_for_sale) {
        return entries;
    }

    pushIfPositive(entries, stats.properties_commercials_count_for_sale, {
        "@type": "PropertyValue",
        name: "Liczba ofert nowych lokali użytkowych w sprzedaży",
        unitText: "oferty"
    });
    pushIfPositive(entries, stats.offers_commercials_count_for_sale, {
        "@type": "PropertyValue",
        name: "Liczba inwestycji deweloperskich",
        unitText: "inwestycje"
    });
    pushIfPositive(entries, stats.properties_commercials_ranges_price_m2_avg, {
        "@type": "PropertyValue",
        name: "Średnia cena za metr kwadratowy",
        unitText: "PLN/m2"
    });
    pushIfPositive(entries, stats.properties_commercials_ranges_price_min, {
        "@type": "PropertyValue",
        name: "Najniższa cena lokalu użytkowego",
        unitText: "PLN"
    });
    pushIfPositive(entries, stats.properties_commercials_ranges_area_min, {"@type": "PropertyValue", name: "Metraż minimalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_commercials_ranges_area_max, {"@type": "PropertyValue", name: "Metraż maksymalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_commercials_count_ready, {
        "@type": "PropertyValue",
        name: "Lokale użytkowe gotowe do odbioru",
        unitText: "lokale"
    });

    const inConstruction =
        (stats.properties_commercials_count_ready_in_3m || 0) +
        (stats.properties_commercials_count_ready_in_6m || 0) +
        (stats.properties_commercials_count_ready_in_12m || 0);
    pushIfPositive(entries, inConstruction, {"@type": "PropertyValue", name: "Lokale użytkowe w trakcie budowy", unitText: "lokale"});

    pushDominantRoomType(entries, stats.properties_commercials_rooms_ranges, stats.properties_commercials_count_for_sale, (roomCount) =>
        getRoomTypeLabel("Lokale użytkowe", roomCount)
    );

    pushIfPositive(entries, stats.promotions_count_total, {"@type": "PropertyValue", name: "Liczba aktualnych ofert promocyjnych", unitText: "oferty"});

    return entries;
};

const getMixedVariableMeasured = (region: ISelectedRegion): IDatasetVariableMeasured[] => {
    const stats = region.stats;
    const entries: IDatasetVariableMeasured[] = [];

    const propertiesCountForSale = (stats?.properties_flats_count_for_sale || 0) + (stats?.properties_houses_count_for_sale || 0);
    const offersCountForSale = (stats?.offers_flats_count_for_sale || 0) + (stats?.offers_houses_count_for_sale || 0);

    if (!propertiesCountForSale || !offersCountForSale) {
        return entries;
    }

    const nonZeroAreaMinValues = [stats?.properties_flats_ranges_area_min || 0, stats?.properties_houses_ranges_area_min || 0].filter((value) => value > 0);
    const areaMin = nonZeroAreaMinValues.length > 0 ? Math.min(...nonZeroAreaMinValues) : 0;
    const areaMax = Math.max(stats?.properties_flats_ranges_area_max || 0, stats?.properties_houses_ranges_area_max || 0);
    const nonZeroPriceMinValues = [stats?.properties_flats_ranges_price_min || 0, stats?.properties_houses_ranges_price_min || 0].filter((value) => value > 0);
    const priceMin = nonZeroPriceMinValues.length > 0 ? Math.min(...nonZeroPriceMinValues) : 0;

    pushIfPositive(entries, propertiesCountForSale, {"@type": "PropertyValue", name: "Liczba ofert nowych nieruchomości w sprzedaży", unitText: "oferty"});
    pushIfPositive(entries, offersCountForSale, {"@type": "PropertyValue", name: "Liczba inwestycji deweloperskich", unitText: "inwestycje"});
    pushIfPositive(entries, stats?.average_price_m2, {"@type": "PropertyValue", name: "Średnia cena za metr kwadratowy", unitText: "PLN/m2"});
    pushIfPositive(entries, priceMin, {"@type": "PropertyValue", name: "Najniższa cena nieruchomości", unitText: "PLN"});
    pushIfPositive(entries, areaMin, {"@type": "PropertyValue", name: "Metraż minimalny", unitText: "MTK"});
    pushIfPositive(entries, areaMax, {"@type": "PropertyValue", name: "Metraż maksymalny", unitText: "MTK"});
    pushIfPositive(entries, stats?.promotions_count_total, {"@type": "PropertyValue", name: "Liczba aktualnych ofert promocyjnych", unitText: "oferty"});

    return entries;
};

export const parseShortDatePL = (value: string): Date | null => {
    const [day, month, year] = value.split(".").map(Number);
    const date = new Date(year, month - 1, day);
    return isValid(date) ? date : null;
};

export const getOfferListDatasetSchema = ({offerType, filters, region, url, updateDate}: IParams): IOfferListDatasetSchema | null => {
    if (region.type !== RegionType.CITY || region.country === Country.SPAIN) {
        return null;
    }

    const reportDate = (updateDate && parseShortDatePL(updateDate)) || new Date();

    let wording: IOfferTypeWording;
    let variableMeasured: IDatasetVariableMeasured[];

    switch (offerType) {
        case OfferType.COMMERCIAL:
            wording = OFFER_TYPE_WORDING.COMMERCIAL;
            variableMeasured = getCommercialVariableMeasured(region);
            break;
        case OfferType.FLAT:
            if (isExactStudioFlatFilter(filters) || isExactMultiRoomFlatFilter(filters) || isOpenEndedRoomsFilter(filters)) {
                return null;
            }
            wording = OFFER_TYPE_WORDING.FLAT;
            variableMeasured = getFlatVariableMeasured(region);
            break;
        case OfferType.HOUSE:
            wording = OFFER_TYPE_WORDING.HOUSE;
            variableMeasured = getHouseVariableMeasured(region);
            break;
        default:
            wording = OFFER_TYPE_WORDING.MIXED;
            variableMeasured = getMixedVariableMeasured(region);
    }

    if (variableMeasured.length === 0) {
        return null;
    }

    return {
        ...buildBaseSchema(region, wording, url, reportDate),
        variableMeasured
    };
};
import {isValid} from "date-fns";

import {formatPL} from "@pg-mono/string-utils";

import {Country} from "../../../../region/types/Country";
import {RegionType} from "../../../../region/types/RegionType";
import {OfferType} from "../../../types/OfferType";
import {ISelectedRegion} from "../../actions/offer_list_selected_data_actions";
import {IOfferListRoomsFilter} from "./constants";
import {isExactMultiRoomFlatFilter, isExactStudioFlatFilter, isOpenEndedRoomsFilter} from "./get_location_generic_description";

interface IParams {
    offerType: OfferType | null;
    filters?: IOfferListRoomsFilter | null;
    region: ISelectedRegion;
    url: string;
    // "dd.MM.yyyy" — the same "Dane aktualne na..." string already shown in the visible content (flat offers
    // only). Parsed back into a Date so datePublished/dateModified/temporalCoverage match it exactly.
    updateDate?: string;
}

interface IDatasetVariableMeasured {
    "@type": "PropertyValue";
    name: string;
    value: number;
    unitText: string;
    description?: string;
}

interface IOfferListDatasetSchema {
    "@context": "https://schema.org/";
    "@type": "Dataset";
    name: string;
    description: string;
    url: string;
    keywords: string[];
    inLanguage: "pl-PL";
    isAccessibleForFree: true;
    datePublished: string;
    dateModified: string;
    temporalCoverage: string;
    creator: {"@type": "Organization"; name: string; url: string};
    publisher: {"@type": "Organization"; name: string; url: string};
    spatialCoverage: {
        "@type": "Place";
        name: string;
        address: {
            "@type": "PostalAddress";
            addressLocality: string;
            addressRegion?: string;
            addressCountry: "PL";
        };
    };
    variableMeasured: IDatasetVariableMeasured[];
}

interface IOfferTypeWording {
    nounPlural: string;
    nounGenitivePlural: string;
}

const OFFER_TYPE_WORDING: Record<"FLAT" | "HOUSE" | "COMMERCIAL" | "MIXED", IOfferTypeWording> = {
    FLAT: {nounPlural: "nowe mieszkania", nounGenitivePlural: "mieszkań"},
    HOUSE: {nounPlural: "nowe domy", nounGenitivePlural: "domów"},
    COMMERCIAL: {nounPlural: "nowe lokale użytkowe", nounGenitivePlural: "lokali użytkowych"},
    MIXED: {nounPlural: "nowe nieruchomości", nounGenitivePlural: "nieruchomości"}
};

const pushIfPositive = (entries: IDatasetVariableMeasured[], value: number | null | undefined, entry: Omit<IDatasetVariableMeasured, "value">) => {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        entries.push({...entry, value});
    }
};

const RP_ORGANIZATION = {"@type": "Organization" as const, name: "RynekPierwotny.pl", url: "https://rynekpierwotny.pl/"};

const buildBaseSchema = (
    region: ISelectedRegion,
    wording: IOfferTypeWording,
    url: string,
    reportDate: Date
): Omit<IOfferListDatasetSchema, "variableMeasured"> => {
    const voivodeship = region.stats?.region_type_voivodeship?.name;
    const dateIso = formatPL(reportDate, "yyyy-MM-dd");

    return {
        "@context": "https://schema.org/",
        "@type": "Dataset",
        name: `Raport rynkowy – ${wording.nounPlural} ${region.name}, ${formatPL(reportDate, "LLLL yyyy")}`,
        description: `Aktualny stan rynku pierwotnego (${wording.nounGenitivePlural}) ${region.name_declension_where}: liczba ofert i inwestycji deweloperskich, ceny (w tym cena za m²), metraże oraz terminy oddania. Dane aktualne na ${dateIso}.`,
        url,
        keywords: [
            `${wording.nounPlural} ${region.name}`,
            `rynek pierwotny ${region.name}`,
            `ceny ${wording.nounGenitivePlural} ${region.name}`,
            `${wording.nounPlural} od dewelopera ${region.name}`,
            `cena za m2 ${region.name}`
        ],
        inLanguage: "pl-PL",
        isAccessibleForFree: true,
        datePublished: dateIso,
        dateModified: dateIso,
        temporalCoverage: dateIso,
        creator: RP_ORGANIZATION,
        publisher: RP_ORGANIZATION,
        spatialCoverage: {
            "@type": "Place",
            name: `${region.name}, Polska`,
            address: {
                "@type": "PostalAddress",
                addressLocality: region.name,
                addressRegion: voivodeship,
                addressCountry: "PL"
            }
        }
    };
};

const getFlatVariableMeasured = (region: ISelectedRegion): IDatasetVariableMeasured[] => {
    const stats = region.stats;
    const entries: IDatasetVariableMeasured[] = [];

    if (!stats?.offers_flats_count_for_sale || !stats?.properties_flats_count_for_sale) {
        return entries;
    }

    pushIfPositive(entries, stats.properties_flats_count_for_sale, {
        "@type": "PropertyValue",
        name: "Liczba ofert nowych mieszkań w sprzedaży",
        unitText: "oferty"
    });
    pushIfPositive(entries, stats.offers_flats_count_for_sale, {"@type": "PropertyValue", name: "Liczba inwestycji deweloperskich", unitText: "inwestycje"});
    pushIfPositive(entries, stats.average_price_m2, {"@type": "PropertyValue", name: "Średnia cena za metr kwadratowy", unitText: "PLN/m2"});
    pushIfPositive(entries, stats.properties_flats_ranges_price_min, {"@type": "PropertyValue", name: "Najniższa cena mieszkania", unitText: "PLN"});

    if (
        typeof stats.average_price_m2_y2y_change === "number" &&
        Number.isFinite(stats.average_price_m2_y2y_change) &&
        stats.average_price_m2_y2y_change !== 0
    ) {
        entries.push({
            "@type": "PropertyValue",
            name: "Zmiana średniej ceny rok do roku",
            value: stats.average_price_m2_y2y_change,
            unitText: "PERCENT"
        });
    }

    pushIfPositive(entries, stats.properties_flats_ranges_area_min, {"@type": "PropertyValue", name: "Metraż minimalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_flats_ranges_area_max, {"@type": "PropertyValue", name: "Metraż maksymalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_flats_count_ready, {"@type": "PropertyValue", name: "Mieszkania gotowe do odbioru", unitText: "lokale"});

    const inConstruction =
        (stats.properties_flats_count_ready_in_3m || 0) + (stats.properties_flats_count_ready_in_6m || 0) + (stats.properties_flats_count_ready_in_12m || 0);
    pushIfPositive(entries, inConstruction, {"@type": "PropertyValue", name: "Mieszkania w trakcie budowy", unitText: "lokale"});

    pushDominantRoomType(entries, stats.properties_flats_rooms_ranges, stats.properties_flats_count_for_sale, (roomCount) =>
        roomCount === 1 ? "Kawalerki" : getRoomTypeLabel("Mieszkania", roomCount)
    );

    pushIfPositive(entries, stats.promotions_count_total, {"@type": "PropertyValue", name: "Liczba aktualnych ofert promocyjnych", unitText: "oferty"});

    return entries;
};

// "5-pokojowe i większe" mirrors how the visible content groups the top room-count bucket for every offer type.
const getRoomTypeLabel = (nounPlural: string, roomCount: number): string =>
    roomCount >= 5 ? `${nounPlural} 5-pokojowe i większe` : `${nounPlural} ${roomCount}-pokojowe`;

// Percentage share isn't spelled out for house/commercial the way it is for flats, but the same per-room-count
// counts ARE already listed on the page (e.g. "domy dwupokojowe - X domów") — this only summarizes that same data.
// Room counts of 5+ can arrive under separate keys (6, 7, 8...) rather than one bucket, so they're folded into "5".
const pushDominantRoomType = (
    entries: IDatasetVariableMeasured[],
    roomRanges: Record<number, number> | undefined,
    total: number | undefined,
    getLabel: (roomCount: number) => string
) => {
    const buckets: Record<number, number> = {};

    Object.entries(roomRanges || {}).forEach(([key, count]) => {
        const room = Number(key);
        if (!Number.isFinite(room) || typeof count !== "number") {
            return;
        }
        const bucket = Math.min(room, 5);
        buckets[bucket] = (buckets[bucket] || 0) + count;
    });

    const dominantRoom = Object.entries(buckets).reduce<{room: number; count: number}>(
        (max, [room, count]) => (count > max.count ? {room: Number(room), count} : max),
        {room: 0, count: 0}
    );

    if (dominantRoom.room > 0 && total && total > 0) {
        entries.push({
            "@type": "PropertyValue",
            name: "Udział najpopularniejszego typu w ofercie",
            value: Number(((dominantRoom.count / total) * 100).toFixed(1)),
            unitText: "PERCENT",
            description: getLabel(dominantRoom.room)
        });
    }
};

const getHouseVariableMeasured = (region: ISelectedRegion): IDatasetVariableMeasured[] => {
    const stats = region.stats;
    const entries: IDatasetVariableMeasured[] = [];

    if (!stats?.offers_houses_count_for_sale || !stats?.properties_houses_count_for_sale) {
        return entries;
    }

    pushIfPositive(entries, stats.properties_houses_count_for_sale, {
        "@type": "PropertyValue",
        name: "Liczba ofert nowych domów w sprzedaży",
        unitText: "oferty"
    });
    pushIfPositive(entries, stats.offers_houses_count_for_sale, {"@type": "PropertyValue", name: "Liczba inwestycji deweloperskich", unitText: "inwestycje"});
    pushIfPositive(entries, stats.average_price_m2, {"@type": "PropertyValue", name: "Średnia cena za metr kwadratowy", unitText: "PLN/m2"});
    pushIfPositive(entries, stats.properties_houses_ranges_price_min, {"@type": "PropertyValue", name: "Najniższa cena domu", unitText: "PLN"});
    pushIfPositive(entries, stats.properties_houses_ranges_area_min, {"@type": "PropertyValue", name: "Metraż minimalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_houses_ranges_area_max, {"@type": "PropertyValue", name: "Metraż maksymalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_houses_count_ready, {"@type": "PropertyValue", name: "Domy gotowe do odbioru", unitText: "lokale"});

    const inConstruction =
        (stats.properties_houses_count_ready_in_3m || 0) + (stats.properties_houses_count_ready_in_6m || 0) + (stats.properties_houses_count_ready_in_12m || 0);
    pushIfPositive(entries, inConstruction, {"@type": "PropertyValue", name: "Domy w trakcie budowy", unitText: "lokale"});

    pushDominantRoomType(entries, stats.properties_houses_rooms_ranges, stats.properties_houses_count_for_sale, (roomCount) =>
        getRoomTypeLabel("Domy", roomCount)
    );

    pushIfPositive(entries, stats.promotions_count_total, {"@type": "PropertyValue", name: "Liczba aktualnych ofert promocyjnych", unitText: "oferty"});

    return entries;
};

const getCommercialVariableMeasured = (region: ISelectedRegion): IDatasetVariableMeasured[] => {
    const stats = region.stats;
    const entries: IDatasetVariableMeasured[] = [];

    if (!stats?.offers_commercials_count_for_sale || !stats?.properties_commercials_count_for_sale) {
        return entries;
    }

    pushIfPositive(entries, stats.properties_commercials_count_for_sale, {
        "@type": "PropertyValue",
        name: "Liczba ofert nowych lokali użytkowych w sprzedaży",
        unitText: "oferty"
    });
    pushIfPositive(entries, stats.offers_commercials_count_for_sale, {
        "@type": "PropertyValue",
        name: "Liczba inwestycji deweloperskich",
        unitText: "inwestycje"
    });
    pushIfPositive(entries, stats.properties_commercials_ranges_price_m2_avg, {
        "@type": "PropertyValue",
        name: "Średnia cena za metr kwadratowy",
        unitText: "PLN/m2"
    });
    pushIfPositive(entries, stats.properties_commercials_ranges_price_min, {
        "@type": "PropertyValue",
        name: "Najniższa cena lokalu użytkowego",
        unitText: "PLN"
    });
    pushIfPositive(entries, stats.properties_commercials_ranges_area_min, {"@type": "PropertyValue", name: "Metraż minimalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_commercials_ranges_area_max, {"@type": "PropertyValue", name: "Metraż maksymalny", unitText: "MTK"});
    pushIfPositive(entries, stats.properties_commercials_count_ready, {
        "@type": "PropertyValue",
        name: "Lokale użytkowe gotowe do odbioru",
        unitText: "lokale"
    });

    const inConstruction =
        (stats.properties_commercials_count_ready_in_3m || 0) +
        (stats.properties_commercials_count_ready_in_6m || 0) +
        (stats.properties_commercials_count_ready_in_12m || 0);
    pushIfPositive(entries, inConstruction, {"@type": "PropertyValue", name: "Lokale użytkowe w trakcie budowy", unitText: "lokale"});

    pushDominantRoomType(entries, stats.properties_commercials_rooms_ranges, stats.properties_commercials_count_for_sale, (roomCount) =>
        getRoomTypeLabel("Lokale użytkowe", roomCount)
    );

    pushIfPositive(entries, stats.promotions_count_total, {"@type": "PropertyValue", name: "Liczba aktualnych ofert promocyjnych", unitText: "oferty"});

    return entries;
};

const getMixedVariableMeasured = (region: ISelectedRegion): IDatasetVariableMeasured[] => {
    const stats = region.stats;
    const entries: IDatasetVariableMeasured[] = [];

    const propertiesCountForSale = (stats?.properties_flats_count_for_sale || 0) + (stats?.properties_houses_count_for_sale || 0);
    const offersCountForSale = (stats?.offers_flats_count_for_sale || 0) + (stats?.offers_houses_count_for_sale || 0);

    if (!propertiesCountForSale || !offersCountForSale) {
        return entries;
    }

    const nonZeroAreaMinValues = [stats?.properties_flats_ranges_area_min || 0, stats?.properties_houses_ranges_area_min || 0].filter((value) => value > 0);
    const areaMin = nonZeroAreaMinValues.length > 0 ? Math.min(...nonZeroAreaMinValues) : 0;
    const areaMax = Math.max(stats?.properties_flats_ranges_area_max || 0, stats?.properties_houses_ranges_area_max || 0);
    const nonZeroPriceMinValues = [stats?.properties_flats_ranges_price_min || 0, stats?.properties_houses_ranges_price_min || 0].filter((value) => value > 0);
    const priceMin = nonZeroPriceMinValues.length > 0 ? Math.min(...nonZeroPriceMinValues) : 0;

    pushIfPositive(entries, propertiesCountForSale, {"@type": "PropertyValue", name: "Liczba ofert nowych nieruchomości w sprzedaży", unitText: "oferty"});
    pushIfPositive(entries, offersCountForSale, {"@type": "PropertyValue", name: "Liczba inwestycji deweloperskich", unitText: "inwestycje"});
    pushIfPositive(entries, stats?.average_price_m2, {"@type": "PropertyValue", name: "Średnia cena za metr kwadratowy", unitText: "PLN/m2"});
    pushIfPositive(entries, priceMin, {"@type": "PropertyValue", name: "Najniższa cena nieruchomości", unitText: "PLN"});
    pushIfPositive(entries, areaMin, {"@type": "PropertyValue", name: "Metraż minimalny", unitText: "MTK"});
    pushIfPositive(entries, areaMax, {"@type": "PropertyValue", name: "Metraż maksymalny", unitText: "MTK"});
    pushIfPositive(entries, stats?.promotions_count_total, {"@type": "PropertyValue", name: "Liczba aktualnych ofert promocyjnych", unitText: "oferty"});

    return entries;
};

export const parseShortDatePL = (value: string): Date | null => {
    const [day, month, year] = value.split(".").map(Number);
    const date = new Date(year, month - 1, day);
    return isValid(date) ? date : null;
};

export const getOfferListDatasetSchema = ({offerType, filters, region, url, updateDate}: IParams): IOfferListDatasetSchema | null => {
    if (region.type !== RegionType.CITY || region.country === Country.SPAIN) {
        return null;
    }

    const reportDate = (updateDate && parseShortDatePL(updateDate)) || new Date();

    let wording: IOfferTypeWording;
    let variableMeasured: IDatasetVariableMeasured[];

    switch (offerType) {
        case OfferType.COMMERCIAL:
            wording = OFFER_TYPE_WORDING.COMMERCIAL;
            variableMeasured = getCommercialVariableMeasured(region);
            break;
        case OfferType.FLAT:
            if (isExactStudioFlatFilter(filters) || isExactMultiRoomFlatFilter(filters) || isOpenEndedRoomsFilter(filters)) {
                return null;
            }
            wording = OFFER_TYPE_WORDING.FLAT;
            variableMeasured = getFlatVariableMeasured(region);
            break;
        case OfferType.HOUSE:
            wording = OFFER_TYPE_WORDING.HOUSE;
            variableMeasured = getHouseVariableMeasured(region);
            break;
        default:
            wording = OFFER_TYPE_WORDING.MIXED;
            variableMeasured = getMixedVariableMeasured(region);
    }

    if (variableMeasured.length === 0) {
        return null;
    }

    return {
        ...buildBaseSchema(region, wording, url, reportDate),
        variableMeasured
    };
};
